import {
  faMinusCircle,
  faPlusCircle,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FuzzySearcher } from '@readcloud/common';
import {
  InputGroup,
  InputGroupPrepend,
  InputGroupText,
  ListGroup,
  Modal,
  ModalBody,
  OverlayTrigger,
  useWarnUserBeforeLeaving,
} from '@readcloud/component-library';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Button,
  Card,
  Input,
  Checkbox,
  Label,
  Separator,
  Switch,
  Large,
  Lead,
  H1,
} from '@readcloud/ui';

import { Cloud, User } from '@readcloud/data';

import React, { FormEvent, useMemo, useRef, useState } from 'react';

import { useDebounce } from 'use-debounce';
import {
  CloudCreationForm,
  CloudUpdateForm,
  CreateCloudOperation,
  DeleteCloudOperation,
  UpdateCloudOperation,
} from '../types';
import { ConfirmationPopoverButton } from './ConfirmationPopoverButton';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@readcloud/ui';

/**
 * Use this to sanitise and transform cloud form data,
 * for example, trimming name and description, replacing any whitespace (new line, tab etc) in name with a single space,
 * and ensuring that the current/creating user is a member and admin.
 */
function normaliseCloudFormData<
  Data extends
    | CloudUpdateForm
    | CloudCreationForm
    | z.infer<typeof CloudFormSchema>
>(data: Data, currentUserId: string): Data {
  return {
    ...data,
    addBookAdminOnly: data.addBookAdminOnly ?? true,
    name: data.name?.trim().replace(/\s/g, ' ') || '',
    description: data.description?.trim() || '',
    members: [...new Set([...(data.members || []), currentUserId])],
    admins: [...new Set([...(data.admins || []), currentUserId])],
    books: data.books || [],
  } as Data;
}

function validateCloudForm(
  values: CloudCreationForm | CloudUpdateForm,
  currentUserId: string
) {
  const errors: Partial<Record<keyof CloudCreationForm, string>> = {};
  const normalisedValues = normaliseCloudFormData(values, currentUserId);

  // TODO: Clarify that this cloud validation logic is correct
  if (!normalisedValues.name.length) {
    errors.name = 'Name is required';
  } else if (normalisedValues.name.length < 3) {
    errors.name = 'Name must be longer than 3 characters';
  } else if (normalisedValues.name.length > 100) {
    errors.name = 'Name must be less than 100 characters long';
  } else if (
    normalisedValues.name.toLowerCase().includes('my purchase') ||
    normalisedValues.name.toLowerCase().includes('my interactive login')
  ) {
    errors.name =
      'My Purchases and My Interactive Logins are reserved names and cannot be used';
  }

  if (normalisedValues.description.length > 999) {
    errors.description = 'Description must be less than 1000 characters long';
  }

  return errors;
}

function MemberRow(props: {
  firstName: string;
  lastName: string;
  isCurrentUser: boolean;
  onSwitchIsAdmin: (isAdmin: boolean) => void;
  isAdmin: boolean;
  onRemove: () => void;
}) {
  return (
    <div
      className="rc-flex rc-items-center rc-w-100 rc-gap-2 rc-justify-between"
      style={{ gap: '1rem', opacity: props.isCurrentUser ? 0.4 : 1 }}
    >
      <span>
        {props.firstName} {props.lastName}{' '}
        {props.isCurrentUser && <small>(you)</small>}
      </span>
      <div className="rc-flex rc-items-center rc-gap-4">
        <div className="rc-flex rc-items-center rc-h-5 rc-gap-2">
          <Checkbox
            disabled={props.isCurrentUser}
            name="isAdmin"
            checked={props.isAdmin}
            onCheckedChange={(checked) => props.onSwitchIsAdmin(!!checked)}
          />
          <label className="rc-leading-4 rc-m-0">Admin</label>
        </div>

        <Button
          size="icon"
          variant="ghost"
          onClick={props.onRemove}
          disabled={props.isCurrentUser}
        >
          <FontAwesomeIcon size="lg" icon={faMinusCircle} />
        </Button>
      </div>
    </div>
  );
}

function MemberAdderButton(props: {
  currentUserId: string;
  knownUsers: User[];
  alreadyAddedMembers: string[];
  onUserSelected: (user: User) => void;
}) {
  const knownUsersWithoutAdded = useMemo(
    () =>
      props.knownUsers.filter(
        (user) =>
          !props.alreadyAddedMembers.find((u) => u === user.id) &&
          props.currentUserId !== user.id
      ),
    [props.knownUsers, props.alreadyAddedMembers, props.currentUserId]
  );
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [userSearchText, setUserSearchText] = useState('');
  const userSearcher = useMemo(() => {
    return new FuzzySearcher(knownUsersWithoutAdded, {
      keySelector: (user) =>
        `${user.firstName} ${user.lastName} ${user.institution || ''} ${
          user.email
        } ${user.id}`,
    });
  }, [knownUsersWithoutAdded]);

  const [debouncedSearchText] = useDebounce(userSearchText, 100);
  const filteredUsers = useMemo(
    () =>
      debouncedSearchText.length
        ? userSearcher.search(debouncedSearchText)
        : knownUsersWithoutAdded,
    [debouncedSearchText, userSearcher]
  );
  return (
    <div ref={ref}>
      <Popover open={show} onOpenChange={(show) => setShow(show)}>
        <PopoverTrigger asChild>
          <Button size="sm" className="rc-flex rc-gap-2">
            <FontAwesomeIcon size="lg" icon={faPlusCircle} /> Add Member
          </Button>
        </PopoverTrigger>
        <PopoverContent className="rc-z-[2100] rc-bg-background rc-rounded rc-max-h-[var(--radix-popover-content-available-height)] rc-overflow-auto p-4">
          <div className="rc-flex rc-flex-col rc-gap-2">
            <div className="rc-flex rc-items-center">
              <Input
                name="cloud-search"
                onChange={(e) => setUserSearchText(e.target.value)}
                value={userSearchText}
                placeholder="Search"
                className="h-100%"
                disabled={!props.knownUsers.length}
              />
            </div>
            {props.knownUsers.length ? (
              <div className="rc-flex rc-flex-col">
                {filteredUsers.map((user) => (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      props.onUserSelected(user);
                    }}
                    key={user.id}
                    className="rc-rounded-none rc-flex rc-flex-col rc-items-start rc-py-2 rc-h-auto"
                  >
                    <span>
                      {user.firstName} {user.lastName}
                    </span>
                    <small className="rc-text-muted">{user.email}</small>
                  </Button>
                ))}
              </div>
            ) : (
              <p className="rc-px-4 rc-py-2 rc-m-0 rc-text-center rc-w-full">
                No other users are known
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Add the form schema
const CloudFormSchema = z.object({
  name: z
    .string()
    .min(3, 'Name must be longer than 3 characters')
    .max(100, 'Name must be less than 100 characters long')
    .refine(
      (name) =>
        !name.toLowerCase().includes('my purchase') &&
        !name.toLowerCase().includes('my interactive login'),
      'My Purchases and My Interactive Logins are reserved names and cannot be used'
    ),
  description: z
    .string()
    .max(999, 'Description must be less than 1000 characters long')
    .default(''),
  addBookAdminOnly: z.boolean(),
  members: z.array(z.string()).default([]),
  admins: z.array(z.string()).default([]),
  books: z.array(z.string()).default([]),
});

function CloudForm<Data extends CloudCreationForm | CloudUpdateForm>(props: {
  canDeleteCloud: boolean;
  currentUserId: string;
  knownUsers: User[];
  onCancel: () => void;
  submitionError?: string;
  onDelete?: () => void;
  onSubmit: (data: z.infer<typeof CloudFormSchema>) => Promise<void>;
  defaultValues: z.infer<typeof CloudFormSchema>;
}) {
  const form = useForm<z.infer<typeof CloudFormSchema>>({
    resolver: zodResolver(CloudFormSchema),
    defaultValues: props.defaultValues,
    mode: 'onChange',
  });

  const membersUserDetails = useMemo(
    () =>
      props.knownUsers
        .filter((user) => form.watch('members').find((u) => u === user.id))
        .sort((a, z) => (a.id === props.currentUserId ? -1 : 0)),
    [props.knownUsers, form.watch('members'), props.currentUserId]
  );

  useWarnUserBeforeLeaving(form.formState.isDirty);
  const container = useRef(null);

  return (
    <Form {...form}>
      <form
        className="rc-flex rc-flex-col rc-w-full rc-h-full readcloud-ui readcloud-ui-reset"
        style={{ gap: '1rem' }}
        onSubmit={form.handleSubmit(props.onSubmit)}
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input required placeholder="Name *" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Input placeholder="Description" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="rc-flex rc-flex-col rc-gap-2">
          {/* TODO: The padding used here to align a caption with the input placeholders is not resilient to changes in the design system */}
          <div className="rc-flex rc-items-end" style={{ gap: '0.5rem' }}>
            <Label className="rc-mb-0">Members</Label>
            <div style={{ flex: 1 }} />

            <MemberAdderButton
              currentUserId={props.currentUserId}
              alreadyAddedMembers={form.watch('members')}
              knownUsers={props.knownUsers}
              onUserSelected={(user) =>
                form.setValue('members', [
                  ...new Set([...form.watch('members'), user.id]),
                ])
              }
            />
          </div>
          <Card
            className="rc-flex rc-flex-col rc-p-3 rc-overflow-auto rc-m-0 rc-shadow-none"
            style={{ height: '12rem', gap: '0.5rem' }}
          >
            {membersUserDetails.map((member) => (
              <MemberRow
                key={member.id}
                isCurrentUser={member.id === props.currentUserId}
                firstName={member.firstName}
                lastName={member.lastName}
                onRemove={() =>
                  form.setValue(
                    'members',
                    form.watch('members').filter((m) => m !== member.id)
                  )
                }
                onSwitchIsAdmin={(isAdmin) =>
                  form.setValue(
                    'admins',
                    !isAdmin
                      ? form.watch('admins').filter((a) => a !== member.id)
                      : [...new Set([...form.watch('admins'), member.id])]
                  )
                }
                isAdmin={!!form.watch('admins').find((m) => m === member.id)}
              />
            ))}
          </Card>
        </div>

        <FormField
          control={form.control}
          name="addBookAdminOnly"
          render={({ field }) => (
            <FormItem className="rc-flex rc-gap-2 rc-items-center rc-justify-between rc-w-full">
              <FormLabel className="rc-leading-4 rc-m-0">
                Members Can Add Content
              </FormLabel>
              <FormControl>
                <Switch
                  checked={!field.value}
                  onCheckedChange={(checked) => field.onChange(!checked)}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <Separator />
        {/* This section, the actions, are in a separate flexed div, paired with a flex-wrap on the parent, so that they show appropriately on a new line on smaller devices */}
        <div className="rc-flex rc-justify-between">
          {props.onDelete && props.canDeleteCloud && (
            <ConfirmationPopoverButton
              confirmationRequired
              variant="destructive"
              backButtonText="Back"
              confirmButtonText="Delete"
              onConfirm={() => {
                props.onDelete && props.onDelete();
              }}
              prompt="All information about this cloud will be lost. Are you sure you want to delete it?"
              data-testid="deleteCloudButton"
            >
              Delete
            </ConfirmationPopoverButton>
          )}
          <div className="rc-flex-1" />
          <div className="rc-flex rc-gap-2">
            <ConfirmationPopoverButton
              confirmationRequired={form.formState.isDirty}
              variant="ghost"
              backButtonText="Back"
              confirmButtonText="Cancel"
              onConfirm={() => {
                form.reset();
                props.onCancel();
              }}
              prompt="You have unsaved changes. Are you sure you'd like to cancel?"
            >
              Cancel
            </ConfirmationPopoverButton>

            <Button
              disabled={
                form.formState.isSubmitting ||
                !form.formState.isValid ||
                (!form.formState.isDirty && !form.formState.isValid)
              }
              type="submit"
            >
              Save
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}

type CloudFormCommonProps = {
  canDeleteCloud: boolean;
  currentUserId: string;
  knownUsers: User[];
  isOpen: boolean;
  onCancel: () => void;
};

function Header(props: { text: React.ReactText }) {
  return (
    <div className="rc-items-baseline rc-flex">
      <h4 className="rc-m-0 rc-text-lg">{props.text}</h4>
    </div>
  );
}

export function UpdateCloudFormModal(
  props: CloudFormCommonProps & {
    previousCloud: Cloud;
    onUpdateCloud: UpdateCloudOperation;
    onDeleteCloud: DeleteCloudOperation;
  }
) {
  const [submitionError, setSubmitionError] = useState<string>();

  const handleSubmit = async (values: z.infer<typeof CloudFormSchema>) => {
    const normalisedValues = normaliseCloudFormData(
      values,
      props.currentUserId
    );
    const onlyWithDifferentFields = Object.fromEntries(
      Object.entries(normalisedValues).filter(
        ([key, val]) =>
          JSON.stringify(props.previousCloud[key]) !== JSON.stringify(val)
      )
    );

    try {
      await props.onUpdateCloud({
        id: props.previousCloud.id,
        ...onlyWithDifferentFields,
      });
    } catch (e) {
      setSubmitionError('Failed to create or update cloud. Please try again.');
      throw Error('Failed to create or update cloud. Please try again.');
    }
  };

  return (
    <Modal isOpen={props.isOpen} size="lg">
      <ModalBody className="rc-flex rc-flex-col rc-gap-4">
        <Large>Edit Cloud</Large>

        <CloudForm
          defaultValues={{
            ...props.previousCloud,
            addBookAdminOnly: props.previousCloud.addBookAdminOnly ?? true,
          }}
          onSubmit={handleSubmit}
          canDeleteCloud={props.canDeleteCloud}
          onDelete={() => props.onDeleteCloud(props.previousCloud.id)}
          onCancel={props.onCancel}
          currentUserId={props.currentUserId}
          knownUsers={props.knownUsers}
          submitionError={submitionError}
        />
      </ModalBody>
    </Modal>
  );
}

export function CreateCloudFormModal(
  props: CloudFormCommonProps & { onCreateCloud: CreateCloudOperation }
) {
  const [submitionError, setSubmitionError] = useState<string>();

  const handleSubmit = async (values: z.infer<typeof CloudFormSchema>) => {
    const normalisedValues = normaliseCloudFormData(
      values,
      props.currentUserId
    ) as CloudCreationForm;

    try {
      await props.onCreateCloud(normalisedValues);
    } catch (e) {
      setSubmitionError('Failed to create or update cloud. Please try again.');
      throw Error('Failed to create or update cloud. Please try again.');
    }
  };

  return (
    <Modal isOpen={props.isOpen} size="lg">
      <ModalBody className="rc-flex rc-flex-col rc-gap-4">
        <>
          <Large>Create cloud</Large>
          <CloudForm
            defaultValues={{
              name: '',
              description: '',
              addBookAdminOnly: true,
              members: [props.currentUserId],
              admins: [props.currentUserId],
              books: [],
            }}
            onSubmit={handleSubmit}
            canDeleteCloud={props.canDeleteCloud}
            onCancel={props.onCancel}
            currentUserId={props.currentUserId}
            knownUsers={props.knownUsers}
            submitionError={submitionError}
          />
        </>
      </ModalBody>
    </Modal>
  );
}
